import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  downloadSandboxTrack,
  getStrategies,
  sandboxDeleteTrack,
  sandboxModerate,
  sandboxRequestModerate,
  sandboxTrackStatus,
  sandboxUploadFileData,
  toSandbox,
} from '../../../../api';
import { RequestModerate } from '../../../../types/requestModerate';
import { Strategy } from '../../../../types/strategy';
import { isSuccessResponse, parseApiError } from '../../../../utils/apiErrors';

export const trackQueryKeys = {
  strategies: ['strategies'] as const,
  status: (strategyId?: string) => ['sandboxTrackStatus', strategyId] as const,
};

export type TrackStatus = {
  error?: string | null;
  loading: boolean;
  percentsDone: number;
};

export type UploadSandboxTrackData = {
  stratId: string;
  markets: number[];
  calcNkd: boolean;
  isAlgo: boolean;
  type: number;
  startingFunds: number;
  fileName?: string;
  fileNames?: string[];
};

class TrackApiError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    Object.setPrototypeOf(this, TrackApiError.prototype);
    this.name = 'TrackApiError';
    this.status = status;
  }
}

const getResponseStatus = (response: unknown): number | undefined => {
  if (response instanceof Error) {
    try {
      return getResponseStatus(JSON.parse(response.message));
    } catch {
      return undefined;
    }
  }

  if (!response || typeof response !== 'object' || !('status' in response)) {
    return undefined;
  }

  const status = (response as { status?: unknown }).status;
  return typeof status === 'number' ? status : undefined;
};

const unwrapApiResponse = <T>(response: unknown): T => {
  if (isSuccessResponse<T>(response)) {
    return response.result;
  }

  throw new TrackApiError(parseApiError(response), getResponseStatus(response));
};

const unwrapDownloadResponse = (response: unknown) => {
  const status = getResponseStatus(response);

  if (status && status >= 400) {
    throw new TrackApiError(status === 404 ? 'Трек не найден' : parseApiError(response), status);
  }

  if (response && typeof response === 'object' && 'success' in response && response.success === false) {
    throw new TrackApiError(parseApiError(response), status);
  }

  return response;
};

export const getTrackErrorMessage = (error: unknown): string => {
  if (error instanceof TrackApiError) {
    return error.message;
  }

  return parseApiError(error);
};

const loadStrategies = async (): Promise<Strategy[]> => {
  const response = await getStrategies();
  return unwrapApiResponse<Strategy[]>(response);
};

const loadSandboxTrackStatus = async (strategyId: string): Promise<TrackStatus> => {
  const response = await sandboxTrackStatus(strategyId);
  return unwrapApiResponse<TrackStatus>(response);
};

const executeAction = async (request: Promise<unknown>): Promise<null> => {
  const response = await request;
  return unwrapApiResponse<null>(response);
};

export const useStrategies = () =>
  useQuery({
    queryKey: trackQueryKeys.strategies,
    queryFn: loadStrategies,
    refetchInterval: 5000,
    refetchIntervalInBackground: true,
  });

export const useSandboxTrackStatus = (strategyId?: string) =>
  useQuery({
    queryKey: trackQueryKeys.status(strategyId),
    queryFn: () => loadSandboxTrackStatus(strategyId as string),
    enabled: Boolean(strategyId),
    refetchInterval: 5000,
    refetchIntervalInBackground: true,
  });

export const useUploadSandboxTrack = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UploadSandboxTrackData) => executeAction(sandboxUploadFileData(data)),
    onSuccess: (_result, data) => {
      queryClient.invalidateQueries({ queryKey: trackQueryKeys.status(data.stratId) });
    },
  });
};

export const useDeleteSandboxTrack = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (strategyId: string) => executeAction(sandboxDeleteTrack(strategyId)),
    onSuccess: (_result, strategyId) => {
      queryClient.invalidateQueries({ queryKey: trackQueryKeys.status(strategyId) });
      queryClient.invalidateQueries({ queryKey: trackQueryKeys.strategies });
    },
  });
};

export const useDownloadSandboxTrack = () =>
  useMutation({
    mutationFn: async (strategyId: string) => {
      try {
        const response = await downloadSandboxTrack(strategyId);
        return unwrapDownloadResponse(response);
      } catch (error) {
        const status = getResponseStatus(error);

        if (status === 404) {
          throw new TrackApiError('Трек не найден', status);
        }

        throw error;
      }
    },
  });

export const useRequestSandboxModerate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: RequestModerate) => executeAction(sandboxRequestModerate(params)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: trackQueryKeys.strategies }),
  });
};

export const useModerateSandbox = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: RequestModerate) => executeAction(sandboxModerate(params)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: trackQueryKeys.strategies }),
  });
};

export const useToSandbox = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { strategyId: string }) => executeAction(toSandbox(data)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: trackQueryKeys.strategies }),
  });
};
